import { useEffect, useRef } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import Modal, { type ModalRef } from '@/components/@IntranetLibrary/Modal';
import MobileBottomSheet from '@/components/@IntranetLibrary/MobileBottomSheet';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import useLayoutSize from '@/hooks/useLayoutSize';
import { formatSerpText } from '@/utils/approval/serpDocument';
import {
  getSerpReturnBoxKeepYn,
  getSerpReturnItems,
  type SerpStoreReturnSource,
} from '@/utils/approval/serpStoreReturn';
import { SerpStoreReturnDetailRoot, SerpStoreReturnSheetBody } from './styles';

type TablesProps = {
  source: SerpStoreReturnSource;
  t: TFunction;
};

/** 레거시 setDetailData 의 표 3개(칸 구성 동일) */
const SerpStoreReturnDetail = ({ source, t }: TablesProps) => {
  const items = getSerpReturnItems(source);
  return (
    <SerpStoreReturnDetailRoot>
      <table className="table table-bordered mb-30 text-center">
        <tbody>
          <tr>
            <th>{t('브랜드')}</th>
            <th>{t('반품 유형')}</th>
            <th>{t('운영시즌')}</th>
            <th>{t('차수')}</th>
          </tr>
          <tr>
            <td>{formatSerpText(source.brandCd)}</td>
            <td>{formatSerpText(source.distTypNm)}</td>
            <td>{formatSerpText(source.operSsnCd)}</td>
            <td>{formatSerpText(source.rtnDeg)}</td>
          </tr>
        </tbody>
      </table>
      <table className="table table-bordered mb-30 text-center">
        <tbody>
          <tr>
            <th>{t('반품 요청 번호')}</th>
            <th>{t('반품명')}</th>
            <th>{t('반품 기간')}</th>
            <th>{t('반품 박스 보관 지시 여부')}</th>
          </tr>
          <tr>
            <td>{formatSerpText(source.rtnReqNo)}</td>
            <td>{formatSerpText(source.tit)}</td>
            <td>{`${formatSerpText(source.staDe)} ~ ${formatSerpText(
              source.endDe,
            )}`}</td>
            <td>{formatSerpText(getSerpReturnBoxKeepYn(source))}</td>
          </tr>
        </tbody>
      </table>
      <label className="label font-weight-bold">{t('반품 대상 리스트')}</label>
      <table className="table table-bordered mb-30 text-center">
        <tbody>
          <tr>
            <th>{t('아이템')}</th>
            <th>{t('상품 시즌')}</th>
            <th>{t('스타일')}</th>
            <th>{t('스타일명')}</th>
            <th>{t('컬러')}</th>
            <th>{t('아웃 일정 계획')}</th>
            <th>{t('캐리 오버')}</th>
          </tr>
          {items.map((item, index) => (
            <tr key={`item-${index}`}>
              <td>
                {formatSerpText(item.itmCd)}
                {item.itmNm ? `(${item.itmNm})` : ''}
              </td>
              <td>{formatSerpText(item.ssnCd)}</td>
              <td>{formatSerpText(item.prodCd)}</td>
              <td>{formatSerpText(item.prodNm)}</td>
              <td>{formatSerpText(item.colorCd)}</td>
              <td>{formatSerpText(item.skdDe)}</td>
              <td>{formatSerpText(item.carryOver)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </SerpStoreReturnDetailRoot>
  );
};

type DetailDialogProps = {
  open: boolean;
  onClose: () => void;
  source: SerpStoreReturnSource | null;
  isLoading?: boolean;
  hasError?: boolean;
};

/** 「자세히 보기」 — PC 모달, 모바일 바텀시트 */
const SerpStoreReturnDetailDialog = ({
  open,
  onClose,
  source,
  isLoading = false,
  hasError = false,
}: DetailDialogProps) => {
  const { t } = useTranslation();
  const { isMobile } = useLayoutSize();
  const modalRef = useRef<ModalRef>(null);

  useEffect(() => {
    if (isMobile) return;
    if (open) modalRef.current?.open();
    else modalRef.current?.close();
  }, [open, isMobile]);

  const renderBody = () => {
    if (source) return <SerpStoreReturnDetail source={source} t={t} />;
    if (hasError) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('데이터를 불러오지 못했습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (isLoading) {
      return (
        <ApprovalNoticeBox role="status">
          {t('데이터를 불러오는 중입니다...')}
        </ApprovalNoticeBox>
      );
    }
    return null;
  };

  if (isMobile) {
    return (
      <MobileBottomSheet
        open={open}
        onClose={onClose}
        title={t('자세히보기')}
        height="auto"
        maxHeight="80vh"
        swipeable={false}
      >
        <SerpStoreReturnSheetBody>{renderBody()}</SerpStoreReturnSheetBody>
      </MobileBottomSheet>
    );
  }
  return (
    <Modal ref={modalRef} title={t('자세히보기')} handleClose={onClose}>
      <>{renderBody()}</>
    </Modal>
  );
};

export default SerpStoreReturnDetailDialog;
